import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { ContentDetailsGlobalStore } from '@bizzdesign/data-access-content-details';
import {
  ActionButtonDirective,
  PreventScrollDirective,
} from '@bizzdesign/shared/directives';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import { NavigationToolbarSettings } from '@bizzdesign/shared/models';
import {
  type ActionButton,
  type BDMenuItem,
  type ContentItem,
  type DisplayText,
  type ToolbarButton,
} from '@bizzdesign/shared/models';
import {
  TranslateActionButtonPipe,
  TranslateDisplayTextPipe,
} from '@bizzdesign/shared/pipes';
import { NavigationToolbarService } from '@bizzdesign/shared/services';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import {
  type Observable,
  combineLatest,
  delay,
  map,
  of,
  switchMap,
} from 'rxjs';
import { FilepathComponent } from '../filepath/filepath.component';
import { SearchableMenuComponent } from '../searchable-menu/searchable-menu.component';
import { MenuComponent } from '@bizzdesign/ui-menu';
import { LogoComponent } from '@bizzdesign/ui-logo';

@Component({
  selector: 'bd-navigation-toolbar',
  imports: [
    AsyncPipe,
    LogoComponent,
    MenuComponent,
    FilepathComponent,
    SearchableMenuComponent,
    ButtonModule,
    SharedI18nModule,
    TranslateActionButtonPipe,
    TranslateDisplayTextPipe,
    ActionButtonDirective,
    TooltipModule,
    PreventScrollDirective,
  ],
  templateUrl: './navigation-toolbar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationToolbarComponent {
  private readonly navigationToolbarService = inject(NavigationToolbarService);
  readonly contentDetailsGlobalStore = inject(ContentDetailsGlobalStore);

  @Input() navigationToolbarSettings: NavigationToolbarSettings =
    new NavigationToolbarSettings();

  readonly breadcrumbItems$: Observable<BDMenuItem[]> =
    this.navigationToolbarService.activeBreadcrumbItems$.pipe(delay(0));
  readonly searchableMenuTopItems$: Observable<BDMenuItem[]> =
    this.navigationToolbarService.activeSearchableMenuTopItems$.pipe(delay(0));
  readonly searchableMenuAllItems$: Observable<BDMenuItem[]> =
    this.navigationToolbarService.activeSearchableMenuAllItems$.pipe(delay(0));
  readonly searchableMenuHasMore$: Observable<boolean> =
    this.navigationToolbarService.activeSearchableMenuHasMore$.pipe(delay(0));
  readonly searchableMenuAllItemsPageSize$: Observable<number> =
    this.navigationToolbarService.activeSearchableMenuAllItemsPageSize$.pipe(
      delay(0),
    );
  readonly searchableMenuTopItemsLoading$: Observable<boolean> =
    this.navigationToolbarService.activeSearchableMenuTopItemsLoading$.pipe(
      delay(0),
    );
  readonly searchableMenuAllItemsLoading$: Observable<boolean> =
    this.navigationToolbarService.activeSearchableMenuAllItemsLoading$.pipe(
      delay(0),
    );
  readonly filepathShowMenu$: Observable<boolean> =
    this.navigationToolbarService.activeFilepathShowMenu$.pipe(delay(0));
  readonly filepathChevronTooltip$: Observable<DisplayText | undefined> =
    this.navigationToolbarService.activeFilepathChevronTooltip$.pipe(delay(0));
  readonly searchableMenuActiveItemId$: Observable<string | undefined> =
    this.navigationToolbarService.activeSearchableMenuActiveItemId$.pipe(
      delay(0),
    );
  readonly searchableMenuSearchPlaceholder$: Observable<
    DisplayText | undefined
  > = this.navigationToolbarService.activeSearchableMenuSearchPlaceholder$.pipe(
    delay(0),
  );
  readonly searchableMenuTopItemsTitle$: Observable<DisplayText | undefined> =
    this.navigationToolbarService.activeSearchableMenuTopItemsTitle$.pipe(
      delay(0),
    );
  readonly searchableMenuNoItemsText$: Observable<DisplayText | undefined> =
    this.navigationToolbarService.activeSearchableMenuNoItemsText$.pipe(
      delay(0),
    );
  readonly searchableMenuFooterAction$: Observable<ActionButton | undefined> =
    this.navigationToolbarService.activeSearchableMenuFooterAction$.pipe(
      delay(0),
    );
  readonly burgerMenuItems$: Observable<BDMenuItem[]> =
    this.navigationToolbarService.activeBurgerMenuItems$.pipe(
      delay(0),
      map((items) =>
        items.map((item) =>
          item.disabled$
            ? item.disabled$.pipe(
                map((disabled): BDMenuItem => ({ ...item, disabled })),
              )
            : of(item),
        ),
      ),
      switchMap((items) => (items.length > 0 ? combineLatest(items) : of([]))),
    );
  readonly toolbarSecondaryButton$: Observable<ToolbarButton | undefined> =
    this.navigationToolbarService.activeToolbarSecondaryButton$.pipe(delay(0));
  readonly toolbarPrimaryButton$: Observable<ToolbarButton | undefined> =
    this.navigationToolbarService.activeToolbarPrimaryButton$.pipe(delay(0));
  readonly showMinimal$: Observable<boolean> =
    this.navigationToolbarService.activeShowMinimal$.pipe(delay(0));
  readonly viewDetailsContentItem$: Observable<ContentItem | undefined> =
    this.navigationToolbarService.activeViewDetailsContentItem$.pipe(delay(0));

  public handleSearchableMenuItemClick(item: BDMenuItem): void {
    item.command?.({});
    this.navigationToolbarService.handleSearchableMenuItemClick(item);
  }

  public handleSearchableMenuSearchChange(term: string): void {
    this.navigationToolbarService.handleSearchableMenuSearchChange(term);
  }

  public handleSearchableMenuLoadMore(): void {
    this.navigationToolbarService.handleSearchableMenuLoadMore();
  }

  public handleBurgerMenuItemClick(item: BDMenuItem): void {
    this.navigationToolbarService.handleBurgerItemClick(item);
  }

  public toggleViewDetails(contentItem: ContentItem): void {
    const isCurrentViewDetailsOpen =
      this.contentDetailsGlobalStore.drawerState().visible &&
      this.contentDetailsGlobalStore.contentItem()?.scope === 'view' &&
      this.contentDetailsGlobalStore.contentItem()?.id === contentItem.id;

    if (isCurrentViewDetailsOpen) {
      this.contentDetailsGlobalStore.updateIsDrawerOpen(false);
      return;
    }

    this.contentDetailsGlobalStore.updateContentItem(contentItem);
    this.contentDetailsGlobalStore.updateScrollToUsedIn(false);
    this.contentDetailsGlobalStore.updateIsDrawerOpen(true);
  }
}
